import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
	Calendar as BigCalendar,
	momentLocalizer
} from 'react-big-calendar'
import 'react-big-calendar/lib/css/react-big-calendar.css';
import moment from 'moment';
import { getAppointment, getAppointments, createAppointment, deleteAppointment, getFirstAvailableSlots } from './../../logic/calendar';
import { useNavigate } from 'react-router-dom';
import CustomAgendaView from './CustomAgendaView.jsx';
import CustomBillingView from './CustomBillingView.jsx';
import { Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CustomToolbar from './CustomToolbar.jsx';
import access from 'safe-access';
import { updateDoctorAvailability } from './../../logic/settings';
import { sortBy, camel } from './../../libs/general';

import Select from 'react-select';

// Enhanced debounce utility with cancellation and immediate execution option
const debounce = (func, delay) => {
	let debounceTimer;
	let lastExecutionTime = 0;
	
	return {
		execute: function() {
			const context = this;
			const args = arguments;
			const now = Date.now();
			
			clearTimeout(debounceTimer);
			
			// If enough time has passed since last execution, execute immediately
			if (now - lastExecutionTime >= delay) {
				lastExecutionTime = now;
				return func.apply(context, args);
			} else {
				// Otherwise, debounce as usual
				debounceTimer = setTimeout(() => {
					lastExecutionTime = Date.now();
					func.apply(context, args);
				}, delay);
			}
		},
		cancel: function() {
			clearTimeout(debounceTimer);
		}
	};
};



// Setup the localizer by providing the moment (or globalize) Object
// to the correct localizer.
moment.locale("en-gb", {
	week: {
		dow: 1 //Monday is the first day of the week.
	}
});

const localizer = momentLocalizer(moment) // or globalizeLocalizer

const formats = {
	agendaHeaderFormat: ({ start, end }, culture, localizer) =>
		`Agenda for ${localizer.format(start, 'ddd MMM Do', culture)}`
}

const eventTypeOptions = [
	{ value: 'filled', label: 'Confirmed appointments' },
	{ value: 'available', label: 'Open time-slots' },
	{ value: 'blocked', label: 'Blocked time-slots' }
];

function Calendar(props) {
	const navigate = useNavigate();
	// Add a ref to track the current doctor ID
	const currentDoctorIdRef = useRef(null);
	// Add refs to manage loading state and cancellation
	const loadingOperationRef = useRef(null);
	const abortControllerRef = useRef(null);
	const operationQueueRef = useRef([]);
	const isProcessingRef = useRef(false);
	const currentTargetRef = useRef({ day: null, view: null });
	
	// Add view state as a separate variable to track current view selection
	const [currentView, setCurrentView] = useState('month');
	// Add a dedicated state for persisting the calendar date across page reloads
	const [persistentDate, setPersistentDate] = useState(null);
	const [state, setState] = useState({
		currentEventTypes: ['available', 'filled', 'blocked', 'auto_available'],
		appointments: [],
		showPopup: false,
		chosenStart: null,
		chosenEnd: null,
		chosenType: null,
		initTime: null,
		cutAppointment: null,
		day: null,
		firstAvailability: [],
		isSmallScreen: false,
		width: 0,
		height: 0,
		showAvailabilityPopup: false,
		resources: [
			{
				resourceId: 'Clinic 1',
				resourceTitle: 'Clinic 1'
			},
			{
				resourceId: 'Clinic 2',
				resourceTitle: 'Clinic 2'
			},
			{
				resourceId: 'Clinic 3',
				resourceTitle: 'Clinic 3'
			},
			{
				resourceId: 'Clinic 4',
				resourceTitle: 'Clinic 4'
			}
		]
	});

	const updateWindowDimensions = useCallback(() => {
		setState(prevState => ({
			...prevState,
			width: window.innerWidth, 
			height: window.innerHeight, 
			isSmallScreen: window.innerWidth < 520
		}));
	}, []);

	const loadAppointmentsForDateRange = useCallback(async (day, view) => {
		// CRITICAL RACE CONDITION FIX: Implement aggressive deduplication
		const targetMoment = moment(day);
		const targetDateStr = targetMoment.format('YYYY-MM-DD');
		
		// Clear ALL pending operations for this view (regardless of date)
		// This ensures only the most recent request for a view gets processed
		const removedOperations = operationQueueRef.current.filter(op => op.view === view);
		operationQueueRef.current = operationQueueRef.current.filter(op => op.view !== view);
		
		if (removedOperations.length > 0) {
			// Operations cleared
		}
		
		// Cancel any currently running operation for any view
		// This is aggressive but necessary to prevent stale data from loading
		if (abortControllerRef.current && loadingOperationRef.current) {
			abortControllerRef.current.abort();
		}
		
		// Update target tracking with the latest request
		currentTargetRef.current = { day, view };
		
		const operationId = `load-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
		
		// Add operation to queue
		operationQueueRef.current.push({ operationId, day, view });
		
		// Process queue if not already processing
		if (!isProcessingRef.current) {
			await processOperationQueue();
		}
	}, [setState, currentView, persistentDate]);

	const processOperationQueue = useCallback(async () => {
		if (isProcessingRef.current) {
			return;
		}
		
		isProcessingRef.current = true;
		
		try {
			while (operationQueueRef.current.length > 0) {
				// Get the next operation from queue
				const operation = operationQueueRef.current.shift();
				if (!operation) break;
				
				const { operationId, day, view } = operation;
				
				await executeLoadOperation(operationId, day, view);
			}
		} finally {
			isProcessingRef.current = false;
		}
	}, [setState, currentView, persistentDate]);

	const executeLoadOperation = useCallback(async (operationId, day, view) => {
		const targetDateStr = moment(day).format('YYYY-MM-DD');
		
		// CRITICAL RACE CONDITION FIX: Cancel any existing loading operation
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
		
		// Create new abort controller for this operation
		const abortController = new AbortController();
		abortControllerRef.current = abortController;
		
		// Set current operation ID for tracking
		loadingOperationRef.current = operationId;
		
		try {
			// Check if operation was cancelled before starting
			if (abortController.signal.aborted) {
				return;
			}
			
			// Set loading state
			setState(prevState => ({ ...prevState, isLoading: true }));
			
			// Initialize appointments array
			const appointments = [];
			
			// Calculate date range based on current view
			let startDate, endDate;
			const viewToUse = view || currentView;
			
			// Get the actual day that's being viewed
			const targetDay = moment(day);
			
			switch(viewToUse) {
				case 'month':
					// For month view, we need to include all visible days
					// which includes days from previous/next months that appear in the calendar grid
					// First day of the calendar grid (might be from previous month)
					startDate = moment(targetDay).startOf('month').startOf('week').toISOString();
					// Last day of the calendar grid (might be from next month)
					endDate = moment(targetDay).endOf('month').endOf('week').toISOString();
					break;
				case 'week':
					startDate = moment(targetDay).startOf('week').toISOString();
					endDate = moment(targetDay).endOf('week').toISOString();
					break;
				case 'day':
					startDate = moment(targetDay).startOf('day').toISOString();
					endDate = moment(targetDay).endOf('day').toISOString();
					break;
				case 'agenda':
				case 'billing':
					// For agenda/billing, expand to cover the whole day
					startDate = moment(targetDay).startOf('day').toISOString();
					endDate = moment(targetDay).endOf('day').toISOString();
					break;
				default:
					startDate = moment(targetDay).startOf('month').startOf('week').toISOString();
					endDate = moment(targetDay).endOf('month').endOf('week').toISOString();
			}
			
			// Check if operation was cancelled after date calculation
			if (abortController.signal.aborted) {
				return;
			}
			
			// Get current doctor ID
			const currentDoctorId = window.localStorage.getItem('currentDoctorId');
			const retrievePast = window.localStorage.getItem('currentRetrievePast');
			
			if (!currentDoctorId) {
				setState(prevState => ({...prevState, isLoading: false, error: "No doctor selected" }));
				return;
			}
			
			// Store cache keys to avoid reloading unnecessarily
			window.localStorage.setItem('myDoctorId', currentDoctorId);
			window.localStorage.setItem('myRetrievePast', retrievePast);
			
			// CRITICAL FIX: For day/week view, always force generation of auto-appointments
			// This ensures green slots are consistently generated and visible
			const forceAutoGeneration = (viewToUse === 'day' || viewToUse === 'week');
			
			// Check if operation was cancelled before API call
			if (abortController.signal.aborted) {
				return;
			}
			
			// Fetch appointments for date range
			const apps = await getAppointments(
				currentDoctorId,
				startDate,
				endDate,
				forceAutoGeneration // Force auto-generation for day/week views
			);
			
			// Check if operation was cancelled after API call
			if (abortController.signal.aborted) {
				return;
			}
			
			// Verify this is still the current operation
			if (loadingOperationRef.current !== operationId) {
				return;
			}
			
			// Add null check to prevent TypeError
			if (apps && Array.isArray(apps)) {

				
				apps.forEach((app) => {
					let title;
					switch (app.type) {
						case "available":
							title = `Available - ${moment(app.fromTimestamp).format('HH:mm')}`;
							break;
						case "auto_available":
							title = `Available - ${moment(app.fromTimestamp).format('HH:mm')}`;
							break;
						case "blocked":
							title = `Blocked - ${moment(app.fromTimestamp).format('HH:mm')}`;
							break;
						case "holiday_blocked":
							title = `Public Holiday - ${moment(app.fromTimestamp).format('HH:mm')}`;
							break;
						default:
							title = app.patient && app.patient.name ? 
								`${camel(app.patient.name.firstName)} ${camel(app.patient.name.lastName)} - ${moment(app.fromTimestamp).format('HH:mm')}`
								: `Unknown - ${moment(app.fromTimestamp).format('HH:mm')}`;
							break;
					}

					appointments.push({
						title,
						idCard: app.patient && app.patient.idCard ? app.patient.idCard : null,
						mobile: app.patient && app.patient.mobile ? app.patient.mobile : null,
						landline: app.patient && app.patient.landline ? app.patient.landline : null,
						pharmacyRemarks: app.pharmacyRemarks ? app.pharmacyRemarks : null,
						creationTimestamp: app.creationTimestamp ? app.creationTimestamp : null,
						start: new Date(moment(app.fromTimestamp).valueOf()),
						end: new Date(moment(app.toTimestamp).valueOf()),
						allDay: false,
						// resourceId: app.clinic,
						type: app.type,
						appointment: app
					});
				});
			} else if (apps instanceof Error) {
				console.error(`Error fetching appointments:`, apps);
				// Only update state if this is still the current operation
				if (loadingOperationRef.current === operationId) {
					setState(prevState => ({
						...prevState, 
						isLoading: false,
						error: "Failed to fetch appointments. Please sign out and sign back in."
					}));
				}
				return;
			}
			
			const newInitTime = window.localStorage['tempFromTimestamp'] ?
				new Date(moment(window.localStorage['tempFromTimestamp']).subtract(1, 'hour').format('YYYY-MM-DD HH:mm:ss'))
				: new Date(moment().subtract(1, 'hour'));
			
			window.localStorage.removeItem('tempFromTimestamp');
			

			
			// ATOMIC STATE UPDATE: Update all state at once to prevent race conditions
			setState(prevState => ({ 
				...prevState,
				appointments,
				isLoading: false,
				error: null,
				initTime: newInitTime,
				day: persistentDate || day
			}));
			
		} catch (e) {
			console.error(`Error in executeLoadOperation:`, e);
			// Only update state if this is still the current operation
			if (loadingOperationRef.current === operationId) {
				setState(prevState => ({
					...prevState, 
					isLoading: false, 
					error: "An error occurred while loading appointments. Please try refreshing or sign out and sign back in."
				}));
			}
		}
	}, [setState, currentView, persistentDate]);

	// Unified navigation handler for all views - no more special day view handling
	const handleNavigate = useCallback((navigateAction) => {
		// Calculate new date based on navigation action
		let newDate;
		if (navigateAction === 'PREV') {
			if (currentView === 'month') {
				newDate = moment(persistentDate || state.day || new Date()).subtract(1, 'month').toDate();
			} else if (currentView === 'week') {
				newDate = moment(persistentDate || state.day || new Date()).subtract(1, 'week').toDate();
			} else {
				newDate = moment(persistentDate || state.day || new Date()).subtract(1, 'day').toDate();
			}
		} else if (navigateAction === 'NEXT') {
			if (currentView === 'month') {
				newDate = moment(persistentDate || state.day || new Date()).add(1, 'month').toDate();
			} else if (currentView === 'week') {
				newDate = moment(persistentDate || state.day || new Date()).add(1, 'week').toDate();
			} else {
				newDate = moment(persistentDate || state.day || new Date()).add(1, 'day').toDate();
			}
		} else if (navigateAction === 'TODAY') {
			newDate = new Date();
		} else if (navigateAction instanceof Date) {
			newDate = navigateAction;
		} else {
			// Default case - use the provided date
			newDate = new Date(navigateAction);
		}
		
		// Update persistent date immediately for all views
		setPersistentDate(newDate);
		
		// Update state and trigger loading for all views using the same approach
		setState(prevState => ({
			...prevState,
			day: newDate,
			isLoading: true
		}));
		
		// Load appointments for the new date
		loadAppointmentsForDateRange(newDate, currentView);
		
	}, [loadAppointmentsForDateRange, currentView, state.day, persistentDate]);

	// Fixing appointment loading and calendar display issues
	useEffect(() => {
		// Skip if hidden
		if (props.hidden) return;
		
		// Check for saved view and date from previous session
		const savedView = window.localStorage.getItem('savedCalendarView');
		const savedDate = window.localStorage.getItem('savedCalendarDate');
		
		if (savedView) {
			setCurrentView(savedView);
			// Remove from localStorage to avoid persistence across sessions
			window.localStorage.removeItem('savedCalendarView');
		}
		
		if (savedDate) {
			const restoredDate = new Date(savedDate);
			
			// Set the dedicated persistent date state
			setPersistentDate(restoredDate);
			
			// Set the general state with the restored date 
			setState(prevState => ({ 
				...prevState, 
				day: restoredDate,
				// Also set initTime to match so scrolling works correctly
				initTime: restoredDate
			}));
			
			 // Load appointments for that date and view
			loadAppointmentsForDateRange(restoredDate, savedView || currentView);
			
			// Remove from localStorage to avoid persistence across unrelated sessions
			window.localStorage.removeItem('savedCalendarDate');
			return; // Skip the default loading since we're restoring a saved state
		}
		
		// Initial load case - only trigger if we have NO target date set anywhere
		// and we're not in the middle of a navigation operation
		if (state.appointments.length === 0 && !state.day && !persistentDate && !isProcessingRef.current) {
			const today = new Date();
			loadAppointmentsForDateRange(today, currentView);
		}
	}, [loadAppointmentsForDateRange, currentView]);

	// Simplified effect to monitor auto_available appointments without causing re-renders
	useEffect(() => {
		// Skip if no appointments, component is hidden, or not in day/week view
		if (state.appointments.length === 0 || props.hidden || (currentView !== 'day' && currentView !== 'week')) return;
		

	}, [state.appointments.length, currentView, props.hidden]);

	// Create a debounced version of the loadAppointments function
	const debouncedLoadAppointments = useCallback(
		debounce((day, view) => {
			loadAppointmentsForDateRange(day, view);
		}, 300), // 300ms debounce time
		[loadAppointmentsForDateRange]
	);

	// Add these memoized handlers to prevent rerenders
	const handleViewChange = useCallback((view) => {
		// MOBILE FIX: Prevent switching to unsupported views on mobile devices
		// This prevents white screen issues when week/month views are selected on small screens
		if (state.isSmallScreen && (view === 'week' || view === 'month')) {
			console.warn(`View '${view}' is not supported on mobile devices. Defaulting to 'day' view.`);
			view = 'day';
		}
		
		setCurrentView(view);
		
		// Only reload appointments if we have a day selected
		const currentDate = persistentDate || state.day || new Date();
		
		// Ensure persistentDate is set when changing views
		if (persistentDate === null) {
			setPersistentDate(currentDate);
		}
		
		// Reload appointments for the current date with the new view
		loadAppointmentsForDateRange(currentDate, view);
	}, [loadAppointmentsForDateRange, state.day, persistentDate, state.isSmallScreen]);

	// Setup window resize listener
	useEffect(() => {
		updateWindowDimensions();
		window.addEventListener('resize', updateWindowDimensions);
		
		return () => {
			window.removeEventListener('resize', updateWindowDimensions);
		};
	}, [updateWindowDimensions]);

	// MOBILE FIX: Handle screen size changes and enforce mobile view restrictions
	useEffect(() => {
		// When screen becomes small, force switch to day view if currently on unsupported view
		if (state.isSmallScreen && (currentView === 'week' || currentView === 'month')) {
			console.warn(`Screen size changed to mobile. Switching from '${currentView}' to 'day' view.`);
			setCurrentView('day');
		}
		// When screen becomes large, switch to month view if currently on day view (optional UX improvement)
		else if (!state.isSmallScreen && currentView === 'day' && state.width > 0) {
			// Only auto-switch to month if we weren't explicitly set to day view on desktop
			// This prevents unwanted view changes when users intentionally selected day view
			const wasInitialLoad = state.width === 0; // First render
			if (wasInitialLoad) {
				setCurrentView('month');
			}
		}
	}, [state.isSmallScreen, state.width, currentView]);

	// MOBILE FIX: Add a safety effect to handle any edge cases where unsupported views might be rendered
	useEffect(() => {
		// Double-check that we're not in an invalid state
		if (state.isSmallScreen && (currentView === 'week' || currentView === 'month')) {
			console.warn(`Safety check: Detected unsupported view '${currentView}' on mobile. Force switching to day view.`);
			setCurrentView('day');
		}
	}, [state.isSmallScreen, currentView]);

	// Dedicated effect to properly watch for doctor changes
	useEffect(() => {
		// Create a doctor change detector function
		function detectDoctorChange() {
			const currentDoctorId = window.localStorage.getItem('currentDoctorId');
			const storedDoctorId = currentDoctorIdRef.current;
			
			// Skip if no doctor selected
			if (!currentDoctorId) return;
			
			// Check if doctor has changed or is first load
			if (currentDoctorId !== storedDoctorId) {
				// Update the stored reference
				currentDoctorIdRef.current = currentDoctorId;
				
				// Force cache reset
				window.localStorage.removeItem('myDoctorId');
				window.localStorage.removeItem('myRetrievePast');
				
				// Reset the calendar to today
				const today = new Date();
				setState(prevState => ({
					...prevState,
					day: today,
					appointments: [],
					isLoading: true
				}));
				
				// Load appointments for the new doctor
				loadAppointmentsForDateRange(today, currentView);
			}
		}
		
		// Run the detection immediately
		detectDoctorChange();
		
		// Set up an interval to check for doctor changes
		const intervalId = setInterval(detectDoctorChange, 1000);
		
		// Clean up on unmount
		return () => clearInterval(intervalId);
	}, [loadAppointmentsForDateRange, currentView]);

	// Handle manual refresh
	useEffect(() => {
		if (props.refreshCounter > 0) {
			const day = state.day || new Date();
			debouncedLoadAppointments(day, currentView);
		}
	}, [props.refreshCounter, debouncedLoadAppointments, currentView, state.day]);

	const handleSelectEvent = async (appointment) => {
		let { type, start, end } = appointment;
		if (appointment && type && start && end) {
			start = moment(start);
			end = moment(end);

			setState(prevState => ({ 
				...prevState, 
				chosenAppointment: appointment, 
				chosenType: 'event', 
				chosenStart: start, 
				chosenEnd: end, 
				showPopup: true 
			}));
		}
	};

	const handleEventStyle = useCallback((event) => {
		if (event.type) {
			if (event.type === "blocked")
				return { style: { cursor: "pointer", backgroundColor: "#FE3636", color: 'white', borderColor: "white", borderRadius: "3px" } };
			else if (event.type === "filled")
				return { style: { cursor: "pointer", backgroundColor: "#F6BF26", color: 'white', borderColor: "white", borderRadius: "3px" } };
			else if (event.type === "available" || event.type === "auto_available")
				return { style: { cursor: "pointer", backgroundColor: "#19d477", color: 'white', borderColor: "white", borderRadius: "3px" } };
		} else return;
	}, []);

	const handleTodayStyle = useCallback((date) => {
		const mDate = moment(date);
		var isCurrentDate = mDate.isSame(new Date(), "day");
		if (isCurrentDate)
			return { style: { backgroundColor: "#F5F5F5", color: '#189AD3' } };
	}, []);

	const handleEventTypeChange = (_currentEventTypes) => {
		const currentEventTypes = _currentEventTypes.map((type) => type.value);
		if (currentEventTypes.indexOf('available') !== -1) currentEventTypes.push('auto_available');
		setState(prevState => ({ ...prevState, currentEventTypes }));
	};

	const handlePrint = () => {
		// Hide all interface elements that shouldn't be printed
		const elementsToHide = document.querySelectorAll('.rbc-btn-group, .calendar__toolbar--nav-buttons button:not(.calendar__toolbar--today-button), .calendar__agenda--functions-container');
		elementsToHide.forEach(element => {
			element.classList.add("noprint");
		});
		
		// Target the specific table we want to print
		const agendaTable = document.querySelector('.rbc-agenda-table');
		if (agendaTable) {
			// Make sure the table is fully expanded
			agendaTable.style.width = '100%';
			agendaTable.style.display = 'table';
			agendaTable.style.tableLayout = 'fixed';
			
			// Apply direct styles to rows to control page breaks
			const rows = agendaTable.querySelectorAll('tr');
			rows.forEach(row => {
				row.style.pageBreakInside = 'avoid';
				row.style.display = 'table-row';
				row.style.borderBottom = '1px solid #343434';
			});
			
			// Make sure table cells have proper padding and borders
			const cells = agendaTable.querySelectorAll('th, td');
			cells.forEach(cell => {
				cell.style.padding = '8px';
				cell.style.borderBottom = '1px solid #343434';
				cell.style.display = 'table-cell';
			});
			
			// Ensure thead and tbody are displayed correctly
			const thead = agendaTable.querySelector('thead');
			if (thead) thead.style.display = 'table-header-group';
			
			const tbody = agendaTable.querySelector('tbody');
			if (tbody) {
				tbody.style.display = 'table-row-group';
				tbody.style.pageBreakInside = 'auto';
			}
		}
		
		// Ensure all containers are set to expand fully for printing
		const containerElements = document.querySelectorAll('.calendar__container, .agenda-wrapper, .custom-agenda, .custom-view-content, .custom-view-container, .rbc-agenda-view');
		containerElements.forEach(container => {
			// Store original values to restore later
			container.dataset.originalHeight = container.style.height;
			container.dataset.originalOverflow = container.style.overflow;
			container.dataset.originalMaxHeight = container.style.maxHeight;
			container.dataset.originalPosition = container.style.position;
			
			// Apply print-friendly styles
			container.style.height = 'auto';
			container.style.maxHeight = 'none';
			container.style.overflow = 'visible';
			container.style.position = 'static';
		});
		
		// Allow a moment for style changes to take effect before printing
		setTimeout(() => {
			window.print();
			
			// Restore original styles after printing (with a delay to ensure print is complete)
			setTimeout(() => {
				// Restore container styles 
				containerElements.forEach(container => {
					if (container.dataset.originalHeight) {
						container.style.height = container.dataset.originalHeight;
					}
					if (container.dataset.originalOverflow) {
						container.style.overflow = container.dataset.originalOverflow;
					}
					if (container.dataset.originalMaxHeight) {
						container.style.maxHeight = container.dataset.originalMaxHeight;
					}
					if (container.dataset.originalPosition) {
						container.style.position = container.dataset.originalPosition;
					}
				});
			}, 1000);
		}, 300);
	};

	const handleSlotClick = ({ start, end, slots }) => {
		if (currentView !== 'month' && start && end && slots && slots.length === 2) {
			start = moment(start);
			end = moment(end);

			setState(prevState => ({ 
				...prevState, 
				chosenType: 'slot', 
				chosenStart: start, 
				chosenEnd: end, 
				showPopup: true 
			}));
		}
	};

	const handleReset = (e) => {
		handlePopupClick(e, 'reset');
	};

	const handleBlocked = (e) => {
		handlePopupClick(e, 'blocked');
	};

	const handleAvailable = (e) => {
		handlePopupClick(e, 'available');
	};

	const handlePopupClick = async (e, type) => {
		e.preventDefault();
		setState(prevState => ({ ...prevState, error: '', isUpdating: true }));

		try {
			let _from = moment(state.chosenStart).format("YYYY-MM-DDTHH:mm:00.000");
			_from = moment(_from).toISOString();

			let _to = moment(state.chosenEnd).format("YYYY-MM-DDTHH:mm:00.000");
			_to = moment(_to).toISOString();

			const docId = window.localStorage.getItem('currentDoctorId');
			
			// We need to ensure the type matches exactly what the GraphQL schema expects
			// For enums in GraphQL, we should pass them as unquoted values
			// For this enum (UpdateAvailabilityType), the backend expects: available, blocked, or reset
			const enumType = type.toLowerCase();
			
			const avail = await updateDoctorAvailability(docId, _from, _to, enumType);

			if (avail instanceof Error) {
				console.error("Failed to update availability:", avail);
				setState(prevState => ({ 
					...prevState, 
					isUpdating: false,
					error: `Failed to update doctor's availability: ${avail.message}` 
				}));
			} else {
				// Clear cache entries to ensure next load gets fresh data
				window.localStorage.removeItem('myDoctorId');
				window.localStorage.removeItem('myRetrievePast');
				
				// Update the UI to indicate success
				setState(prevState => ({ 
					...prevState, 
					showPopup: false, 
					isUpdating: false
				}));
				
				// Store current view and date in localStorage before reload
				window.localStorage.setItem('savedCalendarView', currentView);
				
				// Get the active date from persistentDate which is the authoritative source
				// This fixes the issue with the calendar reverting to today's date
				const dateToSave = persistentDate || state.day || new Date();
				window.localStorage.setItem('savedCalendarDate', dateToSave.toISOString());
				
				// Force a hard refresh of the page to ensure data is reloaded
				setTimeout(() => {
					window.location.reload();
				}, 100);
			}
		} catch (err) {
			console.error("Exception in handlePopupClick:", err);
			setState(prevState => ({ 
				...prevState, 
				isUpdating: false,
				error: `An error occurred: ${err.message}` 
			}));
		}
	};

	const handleNewAppointment = async () => {
		const a = state.chosenAppointment;
		if (a) {
			if (a.type === "filled") {
				// For filled slots: Navigate to edit the existing appointment
				const item = await getAppointment(window.localStorage.getItem('currentDoctorId'), a.creationTimestamp);
				navigate(`/appointments/${a.creationTimestamp}`, { state: { item } });
			} else if (a.type === "available" || a.type === "auto_available") {
				// For available slots: Navigate to create a new appointment
				const item = { 
					type: 'available', 
					fromTimestamp: moment(a.start).toISOString(), 
					toTimestamp: moment(a.end).toISOString() 
				};
				navigate("/appointments/new", { state: { item } });
			}
		}
	};

	const firstAvailability = async () => {
		// Show loading state
		setState(prevState => ({ ...prevState, isLoading: true }));
		
		try {
			// Get the doctor ID from localStorage
			const docId = window.localStorage.getItem('currentDoctorId');
			if (!docId) {
				setState(prevState => ({ 
					...prevState, 
					error: "No doctor selected",
					isLoading: false 
				}));
				return;
			}
			
			// Fetch the first available slots
			const availableSlots = await getFirstAvailableSlots(docId);
			
			if (availableSlots instanceof Error) {
				setState(prevState => ({ 
					...prevState, 
					error: `Failed to get available slots: ${availableSlots.message}`,
					isLoading: false
				}));
				return;
			}
			
			if (!Array.isArray(availableSlots) || availableSlots.length === 0) {
				setState(prevState => ({ 
					...prevState, 
					firstAvailabilityList: [],
					showAvailabilityPopup: true,
					isLoading: false
				}));
				return;
			}
			
			// Format the appointments for display in the popup
			const formattedSlots = availableSlots.map(app => ({
				title: `Available - ${moment(app.fromTimestamp).format('HH:mm')}`,
				idCard: null,
				creationTimestamp: app.creationTimestamp,
				start: new Date(moment(app.fromTimestamp).valueOf()),
				end: new Date(moment(app.toTimestamp).valueOf()),
				allDay: false,
				type: app.type,
				appointment: app
			}));
			
			setState(prevState => ({ 
				...prevState, 
				firstAvailabilityList: formattedSlots, 
				showAvailabilityPopup: true,
				isLoading: false
			}));
		} catch (error) {
			console.error("Error in firstAvailability:", error);
			setState(prevState => ({ 
				...prevState, 
				error: `An unexpected error occurred: ${error.message}`,
				isLoading: false
			}));
		}
	};

	const closeFirstAvailabilityPopup = () => {
		setState(prevState => ({ ...prevState, firstAvailabilityList: [], showAvailabilityPopup: false }));
	};

	const handleFirstAvailabilityClick = (item) => {
		// Close the popup first
		setState(prevState => ({ ...prevState, showAvailabilityPopup: false }));
		
		// Create new appointment data directly
		const appointmentData = { 
			type: 'available', 
			fromTimestamp: moment(item.start).toISOString(), 
			toTimestamp: moment(item.end).toISOString() 
		};
		
		// Navigate directly to new appointment page
		navigate("/appointments/new", { state: { item: appointmentData } });
	};

	const getInitTime = () => {
		const initTime = window.localStorage['tempFromTimestamp'] ?
			new Date(moment(window.localStorage['tempFromTimestamp']).subtract(1, 'hour').format('YYYY-MM-DD HH:mm:ss'))
			: new Date(moment().subtract(1, 'hour'));

		window.localStorage.removeItem('tempFromTimestamp');
		setState(prevState => ({ ...prevState, initTime, day: initTime }));
	};

	const handleCutAppointment = () => {
		setState(prevState => ({ ...prevState, cutAppointment: state.chosenAppointment, showPopup: false }));
	};

	const handlePasteAppointment = async () => {
		const cutAppointment = state.cutAppointment;
		const chosenAppointment = state.chosenAppointment;

		if (cutAppointment && (chosenAppointment.type === 'available' || chosenAppointment.type === 'auto_available')) {
			try {
				// Set updating state to show loading indicator
				setState(prevState => ({ ...prevState, error: '', isUpdating: true }));
				
				cutAppointment.appointment.fromTimestamp = chosenAppointment.appointment.fromTimestamp;
				cutAppointment.appointment.toTimestamp = chosenAppointment.appointment.toTimestamp;
				cutAppointment.appointment.creationTimestamp = chosenAppointment.appointment.creationTimestamp;
				
				const docId = cutAppointment.appointment.docId;

				await createAppointment(docId, cutAppointment.appointment);
				await deleteAppointment(docId, cutAppointment.creationTimestamp);
				
				// Clear cache entries to ensure next load gets fresh data
				window.localStorage.removeItem('myDoctorId');
				window.localStorage.removeItem('myRetrievePast');
				
				// Update the UI to indicate success
				setState(prevState => ({ 
					...prevState, 
					showPopup: false, 
					cutAppointment: null,
					isUpdating: false
				}));
				
				// Store current view and date in localStorage before reload
				window.localStorage.setItem('savedCalendarView', currentView);
				
				// Get the active date from persistentDate which is the authoritative source
				const dateToSave = persistentDate || state.day || new Date();
				window.localStorage.setItem('savedCalendarDate', dateToSave.toISOString());
				
				// Force a hard refresh of the page to ensure data is reloaded
				setTimeout(() => {
					window.location.reload();
				}, 100);
			} catch (err) {
				console.error("Exception in handlePasteAppointment:", err);
				setState(prevState => ({ 
					...prevState, 
					isUpdating: false,
					error: `An error occurred: ${err.message}` 
				}));
			}
		}
	};

	// RACE CONDITION FIX CLEANUP: Ensure all operations are cancelled on unmount
	useEffect(() => {
		return () => {
			// Cancel any ongoing operations
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
			// Clear operation queue
			operationQueueRef.current = [];
			// Reset processing flag
			isProcessingRef.current = false;
		};
	}, []);

	// Memoize filtered events to prevent unnecessary re-renders and flashing
	const filteredEvents = useMemo(() => {
		const filtered = state.appointments.filter(event => state.currentEventTypes.includes(event.type));
		return filtered;
	}, [state.appointments, state.currentEventTypes]);

	// Memoize calendar key to prevent unnecessary re-renders
	const calendarKey = useMemo(() => {
		const dateKey = moment(persistentDate || state.day || new Date()).format('YYYY-MM-DD');
		const eventCount = filteredEvents.length;
		const autoCount = filteredEvents.filter(e => e.type === 'auto_available').length;
		return `${currentView}-${dateKey}-events${eventCount}-auto${autoCount}`;
	}, [currentView, persistentDate, state.day, filteredEvents]);

	return (
		window.localStorage.getItem('currentDoctorId') && !props.hidden ?
			<div className="calendar__container">
				{state.error && (
					<div className="alert alert-danger" role="alert">
						{state.error}
						<button 
							type="button" 
							className="close" 
							onClick={() => setState(prevState => ({...prevState, error: null}))}
							aria-label="Close"
						>
							<span aria-hidden="true">&times;</span>
						</button>
					</div>
				)}
				{state.isLoading && (
					<div className="calendar__loading">Loading appointments...</div>
				)}
				{
					(currentView === "agenda" || currentView === "billing") && !props.hidden ?
						<div className="row calendar__agenda--functions-container noprint">
							{
								currentView === "agenda" ?
									<div className="col-12 col-sm-10 col-md-8">
										<Form.Group className="mb-3">
											<Form.Label htmlFor="eventTypes">Show the following:</Form.Label>
											<Select
												id="eventTypes"
												closeMenuOnSelect={false}
												defaultValue={eventTypeOptions}
												isMulti
												options={eventTypeOptions}
												onChange={handleEventTypeChange}
												placeholder="Click here to choose what to show in the agenda"
											/>
										</Form.Group>
									</div>
									: false
							}

							<div className={`col-${currentView === "agenda" ? "2" : "12"} calendar__agenda--print-container`}>
								<div className="calendar__agenda--print-icon" onClick={handlePrint} to="/settings">
									<FontAwesomeIcon icon="print" />
								</div>
							</div>
						</div>
						: null
				}
				{
					!props.hidden && (
						<div style={{ height: "100%" }}>
							{/* MOBILE FIX: Safety check before rendering calendar views */}
							{(currentView === 'month' || currentView === 'week' || currentView === 'day') && (
								// Additional safety: Don't render month/week views on mobile even if they slip through
								!(state.isSmallScreen && (currentView === 'week' || currentView === 'month')) ? (
									<BigCalendar
										id="calendar"
										localizer={localizer}
										events={filteredEvents}
										onView={handleViewChange}
										view={currentView}
										// CRITICAL FIX: Ensure date prop is always consistent and takes precedence
										date={persistentDate || state.day || new Date()}
										defaultView={state.isSmallScreen ? "day" : "month"}
										startAccessor="start"
										endAccessor="end"
										onSelectEvent={handleSelectEvent}
										eventPropGetter={handleEventStyle}
										dayPropGetter={handleTodayStyle}
										step={parseInt(window.localStorage.getItem('currentDoctorStep'), 10) || 15}
										timeslots={1}
										length={1}
										formats={formats}
										views={
											state.isSmallScreen ? 
												{ day: true } 
												: { month: true, week: true, day: true }
										}
										components={{
											toolbar: props => (
												<CustomToolbar 
													{...props} 
													view={currentView}
													onView={handleViewChange}
													firstAvailability={firstAvailability}
												/>
											)
										}}
										selectable={true}
										onSelectSlot={handleSlotClick}
										onNavigate={handleNavigate}
										scrollToTime={state.initTime}
										// KEY FIX: Ensure navigation is always controlled by our handler
										// This prevents BigCalendar from bypassing our custom navigation logic
										key={calendarKey}
									/>
								) : (
									// MOBILE FALLBACK: Show message if unsupported view is detected on mobile
									<div style={{ 
										padding: '20px', 
										textAlign: 'center', 
										color: '#666',
										backgroundColor: '#f8f9fa',
										borderRadius: '8px',
										margin: '20px 0'
									}}>
										<h3>📱 Mobile View</h3>
										<p>Week and Month views are not supported on mobile devices.</p>
										<p>Please use Day, Agenda, or Billing views for the best mobile experience.</p>
										<button 
											onClick={() => setCurrentView('day')}
											style={{
												padding: '10px 20px',
												backgroundColor: '#189AD3',
												color: 'white',
												border: 'none',
												borderRadius: '5px',
												cursor: 'pointer'
											}}
										>
											Switch to Day View
										</button>
									</div>
								)
							)}

							{/* Render Agenda view as a separate component */}
							{currentView === 'agenda' && (
								<div className="custom-view-container">
									<div className="custom-view-header">
										<CustomToolbar
											date={state.day || new Date()}
											view={currentView}
											onView={handleViewChange}
											onNavigate={handleNavigate} 
											firstAvailability={firstAvailability}
											label={`Agenda for ${moment(state.day || new Date()).format('MMMM D, YYYY')}`}
										/>
									</div>
									<div className="custom-view-content">
										{state.appointments.length > 0 ? (
											<div className="agenda-wrapper">
												<div 
													className="custom-agenda" 
													style={{ 
														padding: '20px', 
														height: 'calc(100vh - 200px)', 
														overflowY: 'auto' 
													}}
												>
													<table className="rbc-agenda-table" style={{width: "100%"}}>
														<thead>
															<tr>
																<th className="rbc-header">Patient</th>
																<th className="rbc-header">ID Card</th>
																<th className="rbc-header">Date</th>
																<th className="rbc-header">Time</th>
																<th className="rbc-header">Mobile</th>
															</tr>
														</thead>
														<tbody>
															{filteredEvents
																.filter(event => 
																	moment(state.day).isSame(moment(event.start), 'day')
																)
																.sort((a, b) => +a.start - +b.start)
																.map((event, idx) => {
																	// Get mobile number with country code
																	let fullMobileNumber = '';
																	if (event.mobile) {
																		if (typeof event.mobile === 'object') {
																			const number = event.mobile.mobileNumber || event.mobile.number || '';
																			const countryCode = event.mobile.countryPrefixCode || '356'; // Default to Malta
																			fullMobileNumber = number ? `+${countryCode}${number}` : '';
																		} else {
																			fullMobileNumber = event.mobile;
																		}
																	}
																	
																	return (
																		<tr 
																			key={`agenda_row_${idx}`}
																			onClick={() => handleSelectEvent(event)}
																			style={{
																				cursor: 'pointer',
																				backgroundColor: 
																					event.type === 'filled' ? '#F6BF2620' : 
																					event.type === 'blocked' ? '#FE363620' :
																					event.type === 'available' || event.type === 'auto_available' ? '#19d47720' : 
																					'transparent'
																			}}
																		>
																			<td>{event.title}</td>
																			<td style={{fontWeight: 'bold'}}>
																				{event.type === 'filled' && event.idCard ? event.idCard : ''}
																			</td>
																			<td>{moment(event.start).format('ddd MMM Do')}</td>
																			<td>{moment(event.start).format('HH:mm')} - {moment(event.end).format('HH:mm')}</td>
																			<td>
																				{event.type === 'filled' && fullMobileNumber ? fullMobileNumber : ''}
																			</td>
																		</tr>
																	);
																})
															}
														</tbody>
													</table>
													{filteredEvents.filter(event => 
														moment(state.day).isSame(moment(event.start), 'day')
													).length === 0 && (
														<div className="no-events-message">
															No events to display for this day.
														</div>
													)}
												</div>
											</div>
										) : (
											<div className="calendar__loading">No appointments found</div>
											)}
									</div>
								</div>
							)}

							{/* Render Billing view as a separate component */}
							{currentView === 'billing' && (
								<div className="custom-view-container">
										<div className="custom-view-header">
											<CustomToolbar
												date={state.day || new Date()}
												view={currentView}
												onView={handleViewChange}
												onNavigate={handleNavigate}
												firstAvailability={firstAvailability}
												label={`Billing Report for ${moment(state.day || new Date()).format('MMMM D, YYYY')}`}
											/>
										</div>
										<div className="custom-view-content">
											{state.appointments.length > 0 ? (
												<div className="billing-wrapper">
													<div 
														className="custom-billing" 
														style={{ 
															padding: '20px', 
															height: 'calc(100vh - 200px)', 
															overflowY: 'auto' 
														}}
													>
														<table className="rbc-agenda-table" style={{width: "100%"}}>
															<thead>
																<tr>
																	<th className="rbc-header">Date</th>
																	<th className="rbc-header">Time</th>
																	<th className="rbc-header">Patient</th>
																	<th className="rbc-header">Doctor Fee</th>
																	<th className="rbc-header">Clinic Fee</th>
																	<th className="rbc-header">Services</th>
																	<th className="rbc-header">Total</th>
																</tr>
															</thead>
															<tbody>
																{state.appointments
																	.filter(event => 
																		event.type === 'filled' &&
																		moment(state.day).isSame(moment(event.start), 'day')
																	)
																	.sort((a, b) => +a.start - +b.start)
																	.map((event, idx) => {
																		const doctorFee = access(event, 'appointment.billing.doctorRate') || 0;
																		const clinicFee = access(event, 'appointment.billing.clinicFee') || 0;
																		const services = access(event, 'appointment.billing.services');
																		
																		let servicesBill = 0;
																		if (services) {
																			for (const service of services) {
																				if (service.bought === "true" || service.bought === true) servicesBill += service.price;
																			}
																		}
																		const total = doctorFee + clinicFee + servicesBill;
																		
																		return (
																			<tr 
																				key={`billing_row_${idx}`}
																				onClick={() => handleSelectEvent(event)}
																				style={{cursor: 'pointer'}}
																			>
																				<td>{moment(event.start).format('ddd MMM Do')}</td>
																					<td>{moment(event.start).format('HH:mm')} - {moment(event.end).format('HH:mm')}</td>
																				<td>{event.title}</td>
																				<td>{doctorFee ? <span>€ {doctorFee}</span> : ''}</td>
																				<td>{clinicFee ? <span>€ {clinicFee}</span> : ''}</td>
																				<td>{servicesBill ? <span>€ {servicesBill}</span> : ''}</td>
																				<td>{total ? <span>€ {total}</span> : ''}</td>
																			</tr>
																		);
																	})
																}
															</tbody>
															<tfoot>
																{(() => {
																	const filledAppointments = state.appointments.filter(event => 
																		event.type === 'filled' &&
																		moment(state.day).isSame(moment(event.start), 'day')
																	);
																	
																	const totals = filledAppointments.reduce((acc, event) => {
																		const doctorFee = access(event, 'appointment.billing.doctorRate') || 0;
																		const clinicFee = access(event, 'appointment.billing.clinicFee') || 0;
																		const services = access(event, 'appointment.billing.services');
																		
																		let servicesBill = 0;
																		if (services) {
																			for (const service of services) {
																				if (service.bought === "true" || service.bought === true) servicesBill += service.price;
																			}
																		}
																		
																		acc.doctorFee += doctorFee;
																		acc.clinicFee += clinicFee;
																		acc.servicesBill += servicesBill;
																		acc.total += doctorFee + clinicFee + servicesBill;
																		
																		return acc;
																	}, { doctorFee: 0, clinicFee: 0, servicesBill: 0, total: 0 });
																	
																	return (
																		<tr className="billing-totals-row">
																			<td className="calendar__billing--totals-cell"></td>
																			<td className="calendar__billing--totals-cell"></td>
																			<td className="calendar__billing--totals-cell"></td>
																			<td className="calendar__billing--totals-cell">{totals.doctorFee ? <span>€ {totals.doctorFee}</span> : ''}</td>
																			<td className="calendar__billing--totals-cell">{totals.clinicFee ? <span>€ {totals.clinicFee}</span> : ''}</td>
																			<td className="calendar__billing--totals-cell">{totals.servicesBill ? <span>€ {totals.servicesBill}</span> : ''}</td>
																			<td className="calendar__billing--totals-cell">{totals.total ? <span>€ {totals.total}</span> : ''}</td>
																		</tr>
																	);
																})()}
															</tfoot>
														</table>
														{state.appointments.filter(event => 
															event.type === 'filled' &&
															moment(state.day).isSame(moment(event.start), 'day')
														).length === 0 && (
															<div className="no-events-message">
																No billing records to display for this day.
															</div>
														)}
													</div>
												</div>
											) : (
												<div className="calendar__loading">No billing records found</div>
											)}
										</div>
									</div>
								)}

							{/* Keep the existing popup windows */}
							{state.showPopup && (
								<div className="calendar__popup--container">
									<div className="calendar__popup--popup">						<p className="calendar__popup--date">{state.chosenStart.format('DD MMM YYYY')}</p>
						<p className="calendar__popup--date">{state.chosenStart.format('HH:mm')} to {state.chosenEnd.format('HH:mm')}</p>

						{/* Wrap all buttons in the new container */}
						<div className="calendar__popup--button-container">
											<button
												id="cut_appointment"
												className={
													(state.chosenType !== 'slot' && (window.localStorage.getItem("userRights")==="admin" || window.localStorage.getItem("userRights")==="limitedadmin" ) && (access(state,'chosenAppointment.type') === 'filled' || ((access(state,'chosenAppointment.type') === 'available' || access(state,'chosenAppointment.type') === 'auto_available') && state.cutAppointment))) ?
														"save-button calendar__popup--cut-appointment-button" :
														"save-button calendar__popup--cut-appointment-button disabled"
												}
												disabled={!(state.chosenType !== 'slot' && (window.localStorage.getItem("userRights")==="admin" || window.localStorage.getItem("userRights")==="limitedadmin" ) && (access(state,'chosenAppointment.type') === 'filled' || ((access(state,'chosenAppointment.type') === 'available' || access(state,'chosenAppointment.type') === 'auto_available') && state.cutAppointment)))}
												onClick={state.cutAppointment && (access(state,'chosenAppointment.type') === 'available' || access(state,'chosenAppointment.type') === 'auto_available') ? handlePasteAppointment : handleCutAppointment}>
												{access(state,'chosenAppointment.type') === 'available' || access(state,'chosenAppointment.type') === 'auto_available' ? 'Paste' : 'Cut'} appointment<span></span>
											</button>
											<button
												id="reset"
												className={
													(window.localStorage.getItem("userRights") === "admin" || window.localStorage.getItem("userRights") === "doctor") && state.chosenType !== 'slot' && access(state, 'chosenAppointment.type') !== 'filled' && access(state, 'chosenAppointment.type') !== 'auto_available' ?
														"save-button calendar__popup--reset-button" :
														"save-button calendar__popup--reset-button disabled"
												}
												disabled={!((window.localStorage.getItem("userRights") === "admin" || window.localStorage.getItem("userRights") === "doctor") && state.chosenType !== 'slot' && access(state, 'chosenAppointment.type') !== 'filled' && access(state, 'chosenAppointment.type') !== 'auto_available')}
												onClick={handleReset}>
												Reset slot<span></span></button>
											<button
												id="mark_available"
												className={
													(window.localStorage.getItem("userRights") === "admin" || window.localStorage.getItem("userRights") === "doctor") && 
													(state.chosenType === 'slot' || 
														access(state, 'chosenAppointment.type') === 'blocked') ?
														"save-button calendar__popup--available-button" :
														"save-button calendar__popup--available-button disabled"
												}
												disabled={!((window.localStorage.getItem("userRights") === "admin" || window.localStorage.getItem("userRights") === "doctor") && 
													(state.chosenType === 'slot' || 
														access(state, 'chosenAppointment.type') === 'blocked'))}
												onClick={handleAvailable}>Mark as available<span></span></button>
											<button
												id="mark_blocked"
												className={
													(window.localStorage.getItem("userRights") === "admin" || window.localStorage.getItem("userRights") === "doctor") && 
													(state.chosenType === 'slot' || 
														access(state, 'chosenAppointment.type') === 'available' || 
														access(state, 'chosenAppointment.type') === 'auto_available') ?
														"save-button calendar__popup--blocked-button" :
														"save-button calendar__popup--blocked-button disabled"
												}
												disabled={!((window.localStorage.getItem("userRights") === "admin" || window.localStorage.getItem("userRights") === "doctor") && 
													(state.chosenType === 'slot' || 
														access(state, 'chosenAppointment.type') === 'available' || 
														access(state, 'chosenAppointment.type') === 'auto_available'))}
												onClick={handleBlocked}>Mark as blocked<span></span></button>
											<button
												id="new_edit_appointment"
												className={
													((window.localStorage.getItem("userRights")==="doctor" && access(state,'chosenAppointment.type') === 'filled') || window.localStorage.getItem("userRights") === "admin" || window.localStorage.getItem("userRights") === "limitedadmin" || window.localStorage.getItem("userRights") === "doctor") && access(state, 'chosenAppointment.type') !== 'blocked' && access(state, 'chosenType') !== 'slot' ?
														"save-button calendar__popup--new-appointment-button" :
														"save-button calendar__popup--new-appointment-button disabled"
												}
												disabled={!(((window.localStorage.getItem("userRights")==="doctor" && access(state,'chosenAppointment.type') === 'filled') || window.localStorage.getItem("userRights") === "admin" || window.localStorage.getItem("userRights") === "limitedadmin" || window.localStorage.getItem("userRights") === "doctor") && access(state, 'chosenAppointment.type') !== 'blocked' && access(state, 'chosenType') !== 'slot')}
												onClick={handleNewAppointment}>{state.chosenAppointment && state.chosenAppointment.type === 'filled' ? 'Edit' : 'New'} appointment<span></span>
											</button>
											<p className="calendar__popup--cancel-button" onClick={() => setState(prevState => ({ ...prevState, showPopup: false }))}>Cancel</p>
										</div>
									</div>
								</div>
							)}
							
							{state.showAvailabilityPopup && (
								<div className="calendar__availability-popup--container">
									<div className="calendar__availability-popup--popup">
										<p className="calendar__availability-popup--title">Next available slots</p>
										<p className="calendar__availability-popup--sub-title">Only showing the first 5 available slots for the next 6 months</p>
										{
											state.firstAvailabilityList && state.firstAvailabilityList.length > 0 ?
												state.firstAvailabilityList.map((item, i) => {
													if (i > 4) return undefined;
													return (
														<div
															className="calendar__availability-popup-item"
															onClick={() => handleFirstAvailabilityClick(item)}
															key={'first_availability_' + i}>
															<p>{moment(item.start).format('Do MMM YYYY - HH:mm')}</p>
														</div>
													);
												})
												: <p>No available slots in the next few months</p>
										}
										<p className="calendar__popup--cancel-button" onClick={closeFirstAvailabilityPopup}>Close</p>
									</div>
								</div>
							)}
						</div>
					)
				}
			</div>
			:
			props.calendarOrTasks === "calendar" ?
				<div className="calendar__no-doctor--container">
					<p>Please choose a doctor from the list</p>
					<FontAwesomeIcon icon="arrow-left" />
				</div>
				: false
	);
}

export default Calendar;